import { type ReactElement } from 'react';
import { Check } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { PlatformBadge } from '@/components/shared/platform';
import { cn } from '@/lib/utils';
import { FAMILY_BADGE_CLASSES } from './constants';
import { GitBadge } from './CellRenderers';

// Reuse the shared file-path option renderer (mono, truncated, titled).
export { FilePathFilterOption } from '@/components/DataTable/alerts/FilterComponents';

interface OptionProps {
  value: string;
  isSelected: boolean;
}

// ── Type + event (full `<family>:<event>` fileCategory) ───────
// Tinted by the family segment (mirrors TypeCell), labelled with the full
// `type:event` token so a selection filters the exact type AND event.
export function FileCategoryFilterOption({
  value,
  isSelected,
}: OptionProps): ReactElement {
  const family = value.split(':')[0] ?? '';
  const badgeClass =
    FAMILY_BADGE_CLASSES[family] ?? 'bg-muted text-muted-foreground';
  return (
    <div className="flex items-center gap-3 flex-1 px-0.5 py-0.5">
      <div className="flex items-center justify-center w-4 h-4 shrink-0">
        {isSelected && (
          <Check className="size-4 text-primary" strokeWidth={2.5} />
        )}
      </div>
      <Badge
        variant="outline"
        className={cn(badgeClass, 'max-w-full truncate')}
        title={value}
      >
        {value}
      </Badge>
    </div>
  );
}

// ── Platform / IDE ────────────────────────────────────────────
export function PlatformFilterOption({
  value,
  isSelected,
}: OptionProps): ReactElement {
  return (
    <div className="flex items-center gap-3 flex-1 px-0.5 py-0.5">
      <div className="flex items-center justify-center w-4 h-4 shrink-0">
        {isSelected && (
          <Check className="size-4 text-primary" strokeWidth={2.5} />
        )}
      </div>
      {/* Git isn't a PLATFORM_REGISTRY platform — render the shared GitBadge so
          the filter's Git chip matches the table cell's git-branch icon
          instead of PlatformBadge's `</>` fallback (KIR-3643). */}
      {value === 'git' ? (
        <GitBadge />
      ) : (
        <PlatformBadge platform={value} showTooltip={false} />
      )}
    </div>
  );
}

// ── Projects (Project entity, KIR-3356) ───────────────────────
interface ProjectFilterOptionProps {
  /** Project display name (the option label). */
  label: string;
  isSelected: boolean;
}

export function ProjectFilterOption({
  label,
  isSelected,
}: ProjectFilterOptionProps): ReactElement {
  return (
    <div className="flex items-center gap-3 flex-1 px-0.5 py-0.5">
      <div className="flex items-center justify-center w-4 h-4 shrink-0">
        {isSelected && (
          <Check className="size-4 text-primary" strokeWidth={2.5} />
        )}
      </div>
      <span className="text-sm truncate" title={label}>
        {label}
      </span>
    </div>
  );
}
